import { Box, Center, useBreakpointValue } from "@chakra-ui/react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import TCPlayer from 'tcplayer.js';
import 'tcplayer.js/dist/tcplayer.min.css';

const StreamPlayer = forwardRef(({ 
  autoplay = true,
  controls = false,
  fluid = true,
  responsive = true,
  muted = false,
  poster,
  onPlayerReady = () => {}, 
  onMediaReady = () => {}, 
  onError = (error) => {},
  onMutedChanged = (muted) => {}, 
  onLoadingChanged = (loading) => {},
  onPlayingChanged = (playing) => {},
  onPlayChanged = (playing) => {},
}, ref) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const initializedRef = useRef(false);
  const callbacksRef = useRef({
    onPlayingChanged,
    onMutedChanged,
    onMediaReady,
    onError,
    onPlayerReady
  });
  // Source management refs
  const currentSrcRef = useRef(null);
  const pendingSrcRef = useRef(null);
  
  // Responsive layout - same as StreamDetail
  const isPortrait = useBreakpointValue({ base: true, lg: false });
  const [isPlaying, setIsPlaying] = useState(false);

  // Initialize the player instance with enhanced configuration
  const playerConfig = useMemo(() => ({
    licenseUrl: 'https://license.vod-pro.com/license/v2/1354869623_1/v_cube.license', // Required license URL
    autoplay,
    controls,
    fluid,
    responsive,
    language: "zh-TW",
    reportable: false,
    muted,
    // Additional error recovery options
    // Preload settings
    preload: 'auto',
    // Crossorigin settings for CORS
    crossorigin: 'anonymous',
    // Poster configuration
    ...(poster && { poster }),
    // Disable fallback to video element
    webrtcConfig: {
      fallback: false,
      showLog: false,
      receiveSEI: true,
      connectRetryDelay: 1,
      connectRetryCount: 3,
    }
  }), [autoplay, controls, fluid, responsive, muted, poster]);

  // Update callbacks ref when props change
  useEffect(() => {
    callbacksRef.current = {
      onPlayChanged,
      onPlayingChanged,
      onMutedChanged,
      onLoadingChanged,
      onMediaReady,
      onError,
      onPlayerReady
    };
  }, [onPlayChanged, onPlayingChanged, onMutedChanged, onLoadingChanged, onMediaReady, onError, onPlayerReady]);

  // Create stable event handlers that use refs
  const handlePlay = useCallback((data) => {
    console.log('[StreamPlayer] handlePlay', data);
    callbacksRef.current.onPlayChanged(true);
  }, []);

  const handlePlaying = useCallback((data) => {
    callbacksRef.current.onPlayingChanged(true);
    setIsPlaying(true);
  }, []);

  const handleWebRTCEvent = useCallback((data) => {
    // console.log('[StreamPlayer] handleWebRTCEvent', data);
  }, []);

  const handlePause = useCallback(() => {
    callbacksRef.current.onPlayingChanged(false);
    setIsPlaying(false);
  }, []);

  const handleEnded = useCallback(() => {
    callbacksRef.current.onPlayingChanged(false);
    setIsPlaying(false);
  }, []);

  const handleVolumeChange = useCallback(() => {
    callbacksRef.current.onMutedChanged(playerRef.current?.muted() || false);
  }, []);

  const handleCanPlay = useCallback((data) => {    
    // Process any pending source change
    if (pendingSrcRef.current && pendingSrcRef.current !== currentSrcRef.current) {
      currentSrcRef.current = pendingSrcRef.current;
      pendingSrcRef.current = null;
      
      // Apply the pending source change
      if (playerRef.current) {
        playerRef.current.src(currentSrcRef.current);
        return; // Exit early, wait for next canplay event
      }
    }
    
    callbacksRef.current.onMediaReady(playerRef.current);
  }, []);

  const handleWaiting = useCallback(() => {
    console.log('[StreamPlayer] handleWaiting');
  }, []);

  const handleLoadStart = useCallback((data) => {
    console.log('[StreamPlayer] handleLoadStart', data);
  }, []);

  const handleError = useCallback((error) => {
    callbacksRef.current.onLoadingChanged(false);
    callbacksRef.current.onError(error);
    setIsPlaying(false);
  }, []);

  // Expose player methods via ref
  useImperativeHandle(ref, () => ({
    get player() {
      return playerRef.current;
    },
    get isPlaying() {
      return isPlaying;
    },
    get isReady() {
      return !!playerRef.current;
    },
    get isMuted() {
      return playerRef.current?.muted();
    },
    get posterUrl() {
      return playerRef.current?.poster();
    },
    src: (newUrl) => {
      if (newUrl) {
        if (playerRef.current) {
          // If player is currently loading, queue the new source
          if (playerRef.current.isPlaying) {
            pendingSrcRef.current = newUrl;
            return;
          }
          
          // Set loading state and apply source immediately
          currentSrcRef.current = newUrl;
          pendingSrcRef.current = null;
          playerRef.current.src(newUrl);
        }
      } else {
        // Return our tracked current source, not the player's src()
        return currentSrcRef.current;
      }
    },
    play: () => {
      playerRef.current?.play();
    },
    pause: () => {
      playerRef.current?.pause();
    },
    muted: (value) => {
      playerRef.current?.muted(value);
    },
    on: (event, callback) => {
      playerRef.current?.on(event, callback);
    },
    off: (event, callback) => {
      playerRef.current?.off(event, callback);
    },
    poster: (value) => {
      playerRef.current?.poster(value);
    }
  }), []);

  // Refactored: initializePlayer as useCallback
  const initializePlayer = useCallback(async () => {
    try {
      // Prevent multiple initializations
      if (initializedRef.current || playerRef.current) {
        return;
      }

      // Ensure video element exists before proceeding
      if (!videoRef.current) {
        console.warn('[StreamPlayer] Video ref not available');
        return;
      }

      // Check if video element still has the expected ID (TCPlayer might have modified it)
      if (!videoRef.current.id || !videoRef.current.id.startsWith('tc-player')) {
        console.warn('[StreamPlayer] Video element ID changed or invalid:', videoRef.current.id);
        return;
      }

      playerRef.current = TCPlayer(videoRef.current.id, playerConfig);

      // Mark as initialized
      initializedRef.current = true;

      playerRef.current.on('play', handlePlay);
      playerRef.current.on('playing', handlePlaying);
      playerRef.current.on('pause', handlePause);
      playerRef.current.on('ended', handleEnded);
      playerRef.current.on('volumechange', handleVolumeChange);
      playerRef.current.on('canplay', handleCanPlay);
      playerRef.current.on('waiting', handleWaiting);
      playerRef.current.on('loadstart', handleLoadStart);
      playerRef.current.on('error', handleError);
      playerRef.current.on('webrtcevent', handleWebRTCEvent);

      playerRef.current.on('loadedmetadata', () => {
      });

      playerRef.current.on('suspend', () => {
        setIsPlaying(false);
      });

      playerRef.current.on('abort', () => {
        setIsPlaying(false);
      });

      // Call the ready callback
      callbacksRef.current.onPlayerReady();
    } catch (error) {
      console.error('[StreamPlayer] Failed to initialize player:', error);
      callbacksRef.current.onError(error);
    }
  }, [playerConfig, handlePlay, handlePause, handleEnded, handleVolumeChange, handleCanPlay, handleWaiting, handleLoadStart, handleError]);

  useEffect(() => {
    if (videoRef.current) {
      initializePlayer();
    }

    // Cleanup on unmount
    return () => {
      if (playerRef.current && typeof playerRef.current.dispose === 'function') {
        try {
          playerRef.current.dispose();
        } catch (e) {
          console.error('[StreamPlayer] Error disposing player on cleanup:', e);
        }
        playerRef.current = null;
      }
      initializedRef.current = false;
    };
  }, [initializePlayer]);

  return (
    <Center 
      width="100vw" 
      height="100vh"
      position="absolute"
      top={0}
      left={0}
      bg="black"
      overflow="hidden"
    >
      <Box
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <video
          id="tc-player"
          ref={videoRef}
          preload="auto"
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          style={{ 
            width: "100%",
            height: "100%",
            objectFit: "contain"
          }}
        />
      </Box>
    </Center>
  );
});

StreamPlayer.displayName = 'StreamPlayer';

export default StreamPlayer;